import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const PRESET_COLORS = [
  '#ffc989',
  '#7cc5dc',
  '#75ffbb',
  '#ed773c',
  '#61c3f8',
  '#9baad5',
  '#945a4d',
  '#22b781',
  '#742cbd',
  '#a2a9e5',
  '#960000',
  '#9e3535',
  '#8c5858',
  '#7f8f51',
  '#a33232',
  '#c95959',
  '#4a4343',
  '#ef1d07',
  '#f4ff30',
  '#8e8e8e',
  '#0000ff',
];

interface LayerColorPickerProps {
  color: string;
  onSelect: (color: string) => void;
  disabled?: boolean;
}

export const LayerColorPicker: React.FC<LayerColorPickerProps> = ({
  color,
  onSelect,
  disabled = false,
}) =>
{
  const [open, setOpen] = useState(false);
  const [customColor, setCustomColor] = useState(color);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() =>
  {
    setCustomColor(color);
  }, [color]);

  useEffect(() =>
  {
    if (!open) return;
    const close = (e: MouseEvent) =>
    {
      const popover = document.querySelector('[data-color-popover]');
      if (popover && popover.contains(e.target as Node)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleOpen = () =>
  {
    if (disabled || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverH = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > popoverH ? rect.bottom + 4 : rect.top - popoverH - 4;
    setPopoverPos({ top, left: rect.left });
    setOpen(true);
  };

  const handleSelect = (c: string) =>
  {
    onSelect(c);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    setCustomColor(e.target.value);
  };

  const handleCustomBlur = () =>
  {
    onSelect(customColor);
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Tab inside the popover: close and return focus to trigger so Tab moves to next element
  const handlePopoverKeyDown = (e: React.KeyboardEvent) =>
  {
    if (e.key === 'Escape')
    {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
    else if (e.key === 'Tab')
    {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        title="Click or press Enter to pick a layer color"
        disabled={disabled}
        onClick={handleOpen}
        onKeyDown={e =>
        {
          if ((e.key === 'Enter' || e.key === ' ') && !open)
          {
            e.preventDefault();
            handleOpen();
          }
        }}
        className="w-[26px] h-[26px] rounded border border-[#bbb] flex-shrink-0 disabled:opacity-40 disabled:cursor-default focus:outline focus:outline-2 focus:outline-[#62aacb]"
        style={{ backgroundColor: color }}
      />

      {open &&
        createPortal(
          <div
            data-color-popover
            className="fixed z-[10000] bg-white border border-[#ccc] rounded shadow-lg p-3"
            style={{ top: popoverPos.top, left: popoverPos.left, width: 200 }}
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={handlePopoverKeyDown}
          >
            {/* Preset grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => handleSelect(c)}
                  className="w-6 h-6 rounded border-2 transition-transform hover:scale-110 focus:outline focus:outline-2 focus:outline-[#62aacb]"
                  style={{
                    backgroundColor: c,
                    borderColor: c === color ? '#333' : 'transparent',
                  }}
                />
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-[#eee] my-2" />

            {/* Custom color input */}
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customColor}
                onChange={handleCustomChange}
                onBlur={handleCustomBlur}
                className="w-8 h-8 rounded cursor-pointer border border-[#ccc] p-0"
              />
              <span className="text-[11px] text-[#777]">Custom color</span>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
